// @file Surface-specific strategies for AI chat (board vs whiteboard).
// Consumed by surface_ai_chat_store — use the store, not this composable directly.
import type { WidgetType } from '@@/bits/surface_ai_chat_widgets'
import { useSurfaceStore } from '@@/pinia/surface'
import type { Suggestion } from '@@/pinia/surface_ai_chat_store'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'
import { useBoardAIChatSurfaceStrategy } from './useBoardAIChatSurfaceStrategy'
import { useWhiteboardAIChatSurfaceStrategy } from './useWhiteboardAIChatSurfaceStrategy'

type AIChatSurfaceType = 'board' | 'whiteboard'

interface AIChatIntroductionConfig {
  title: string
  description: string
  manualMessagePlaceholder: string
}

interface AIChatPersonalizedTool {
  widgetType: WidgetType
  icon: string
  text: string
  message: string
  eventProps?: Record<string, string>
}

interface AIChatSurfaceStrategyReturn {
  surfaceType: AIChatSurfaceType
  xAiChat: ComputedRef<boolean>
  introduction: AIChatIntroductionConfig
  fallbackSuggestions: Suggestion[]
  shouldFetchSuggestionsFromApi: ComputedRef<boolean>
  personalizedTools: ComputedRef<AIChatPersonalizedTool[]>
}

function useAIChatSurfaceStrategy(): { aiChatSurfaceStrategy: ComputedRef<AIChatSurfaceStrategyReturn> } {
  const surfaceStore = useSurfaceStore()

  // Instantiate both strategies upfront (both are lightweight -- computed refs over
  // singleton Pinia stores). Use a computed so the active strategy tracks
  // surfaceStore.isWhiteboard reactively, avoiding the race condition where the
  // store initializes before isWhiteboard is true.
  const boardStrategy = useBoardAIChatSurfaceStrategy()
  const whiteboardStrategy = useWhiteboardAIChatSurfaceStrategy()

  const aiChatSurfaceStrategy = computed<AIChatSurfaceStrategyReturn>(() =>
    surfaceStore.isWhiteboard ? whiteboardStrategy : boardStrategy,
  )

  return { aiChatSurfaceStrategy }
}

export { useAIChatSurfaceStrategy }
export type { AIChatIntroductionConfig, AIChatPersonalizedTool, AIChatSurfaceStrategyReturn, AIChatSurfaceType }
